import apiClient, { API_ENDPOINTS } from './config';
import { getStoredAuthUser } from '../utils/authStorage';

const ACTIVE_ORGANIZATION_HEADER = 'X-Active-Organization-Id';

export interface UserListItem
{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_admin: boolean;
    created_at: string;
    role?: string;
    is_active?: boolean;
    full_name?: string;
    organization_name?: string;
    organization_ids?: number[];
    organizations?: Array<{
        organization_id: number;
        organization_name?: string;
        role?: string;
    }>;
}

export interface SelfDeletionOrganizationImpact
{
    organization_id: number;
    organization_name: string;
    role: string;
    sole_admin: boolean;
    will_delete_organization: boolean;
    active_job_ids: number[];
    active_job_statuses: string[];
}

export interface SelfDeletionPreview
{
    user_id: number;
    organizations: SelfDeletionOrganizationImpact[];
    organizations_to_delete: SelfDeletionOrganizationImpact[];
    organizations_to_remove_from: SelfDeletionOrganizationImpact[];
    running_jobs: Array<{
        organization_id: number;
        organization_name: string;
        job_id: number;
        status: string;
    }>;
}

export interface PagedResponse<T>
{
    items: T[];
    total: number;
    page: number;
    page_size: number;
}

export type SortDirection = 'asc' | 'desc';

type OrganizationOption = {
    id: number;
    name?: string;
};

export type OrganizationMembershipRequest = {
    organization_id: number;
    role: 'admin' | 'user';
};

type MembershipPayload = {
    email: string;
    username?: string;
    temp_password?: string;
    full_name?: string;
    team_name: string;
    role: 'admin' | 'user';
    organization_ids?: number[];
    organization_memberships?: OrganizationMembershipRequest[];
};

export interface UpdateUserRequest extends Partial<UserListItem>
{
    temporary_password?: string;
    is_admin?: boolean;
    organization_id?: number;
    organization_ids?: number[];
    organization_memberships?: OrganizationMembershipRequest[];
    current_organization_ids?: number[];
}

function normalizeOrganizationIds(ids: Array<number | string | null | undefined>): number[]
{
    return Array.from(new Set(
        ids
            .map((id) => Number(id || 0))
            .filter((id) => Number.isInteger(id) && id > 0)
    ));
}

function getStoredOrganizationId(): number
{
    try
    {
        const parsed = getStoredAuthUser<any>();
        if (!parsed)
        {
            return 0;
        }

        return Number(parsed?.organization_id || parsed?.active_organization_id || 0);
    }
    catch
    {
        return 0;
    }
}

function getStoredUser(): any | null
{
    return getStoredAuthUser<any>();
}

function isGlobalSuperAdminContext(): boolean
{
    const storedUser = getStoredUser();
    const role = String(storedUser?.role || '').trim().toLowerCase();
    const activeOrganizationId = Number(storedUser?.active_organization_id || storedUser?.organization_id || 0);

    return role === 'superadmin' && !activeOrganizationId;
}

async function fetchOrganizationOptions(): Promise<OrganizationOption[]>
{
    const { data } = await apiClient.get<{
        items?: OrganizationOption[];
    }>(API_ENDPOINTS.ORGANIZATIONS, {
        params: { page: 1, page_size: 200 }
    });

    return Array.isArray(data?.items) ? data.items : [];
}

async function resolveOrganizationIds(payload: CreateUserRequest | UpdateUserRequest): Promise<number[]>
{
    const explicitIds = normalizeOrganizationIds([
        payload.organization_id,
        ...(Array.isArray(payload.organization_ids) ? payload.organization_ids : []),
        ...(Array.isArray(payload.organization_memberships)
            ? payload.organization_memberships.map((membership) => membership.organization_id)
            : []),
    ]);

    if (explicitIds.length > 0)
    {
        return explicitIds;
    }

    const requestedNames = Array.from(new Set([
        String((payload as CreateUserRequest).organization_name || '').trim().toLowerCase(),
        ...((payload as CreateUserRequest).organization_names || []).map((name) => String(name || '').trim().toLowerCase()),
    ].filter((name) => !!name)));

    const organizationOptions = await fetchOrganizationOptions();
    const matchedIds = requestedNames.length > 0
        ? normalizeOrganizationIds(
            organizationOptions
                .filter((org) => requestedNames.includes(String(org.name || '').trim().toLowerCase()))
                .map((org) => org.id)
        )
        : [];

    if (matchedIds.length > 0)
    {
        return matchedIds;
    }

    const storedOrganizationId = getStoredOrganizationId();
    if (storedOrganizationId > 0)
    {
        return [storedOrganizationId];
    }

    try
    {
        const meOrgResponse = await apiClient.get<{ id: number }>(API_ENDPOINTS.ORGANIZATION_ME);
        const meOrgId = Number(meOrgResponse?.data?.id || 0);
        if (meOrgId > 0)
        {
            return [meOrgId];
        }
    }
    catch
    {
        // Ignore /me lookup failures and fall back to the first available organization.
    }

    return normalizeOrganizationIds([organizationOptions[0]?.id]);
}

function buildMembershipPayload(
    payload: Pick<CreateUserRequest, 'email' | 'first_name' | 'last_name' | 'temporary_password' | 'is_admin' | 'organization_memberships'>
): MembershipPayload
{
    const fullName = `${payload.first_name} ${payload.last_name}`.trim();
    const username = String(payload.email || '').split('@')[0];

    return {
        email: payload.email,
        username,
        temp_password: payload.temporary_password,
        full_name: fullName || undefined,
        team_name: '',
        role: payload.is_admin ? 'admin' : 'user',
        ...(payload.organization_memberships ? { organization_memberships: payload.organization_memberships } : {}),
    };
}

function buildOrganizationScopedConfig(orgId: number)
{
    if (isGlobalSuperAdminContext())
    {
        return {
            headers: {
                [ACTIVE_ORGANIZATION_HEADER]: '0',
            },
        };
    }

    return {
        headers: {
            [ACTIVE_ORGANIZATION_HEADER]: String(orgId),
        },
    };
}

async function addUserMembership(orgIds: number[], payload: MembershipPayload)
{
    const [primaryOrgId, ...additionalOrgIds] = orgIds;
    const body: MembershipPayload = {
        ...payload,
        ...(!payload.organization_memberships && additionalOrgIds.length > 0 ? { organization_ids: additionalOrgIds } : {}),
    };
    return apiClient.post(
        `${API_ENDPOINTS.ORGANIZATION_DETAIL(primaryOrgId)}/users`,
        body,
        buildOrganizationScopedConfig(primaryOrgId)
    );
}

async function removeUserMembership(orgId: number, userId: number | string)
{
    await apiClient.delete(
        `${API_ENDPOINTS.ORGANIZATION_DETAIL(orgId)}/users/${userId}`,
        buildOrganizationScopedConfig(orgId)
    );
}

export interface UserFilterParams
{
    search?: string;
    role?: string;
    organization_id?: number;
    date_from?: string;
    date_to?: string;
}

export async function fetchUsers(page: number, pageSize: number, sortBy: string, sortDir: SortDirection, filters?: UserFilterParams)
{
    const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
    };
    if (filters?.search) params.search = filters.search;
    if (filters?.role) params.role = filters.role;
    if (filters?.organization_id) params.organization_id = filters.organization_id;
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;

    const { data } = await apiClient.get<PagedResponse<UserListItem>>(
        API_ENDPOINTS.USERS,
        { params }
    );

    const items = data.items.map((user) => {
        const fullName = String(user.full_name || '').trim();
        const nameParts = fullName ? fullName.split(/\s+/) : [];
        const firstName = String(user.first_name || nameParts[0] || '').trim();
        const lastName = String(user.last_name || nameParts.slice(1).join(' ') || '').trim();
        const normalizedRole = String(user.role || '').trim().toLowerCase();

        return {
            ...user,
            first_name: firstName,
            last_name: lastName,
            is_admin: typeof user.is_admin === 'boolean'
                ? user.is_admin
                : (normalizedRole === 'admin' || normalizedRole === 'superadmin'),
            organization_name: String(user.organization_name || '').trim(),
        };
    });

    return {
        ...data,
        items,
    };
}

export async function updateUser(
    id: number | string,
    payload: UpdateUserRequest
)
{
    const hasOrganizationUpdates = (
        Array.isArray(payload.organization_memberships)
        || Array.isArray(payload.organization_ids)
        || payload.organization_id !== undefined
        || Array.isArray(payload.current_organization_ids)
        || Array.isArray(payload.organizations)
    );

    if (!hasOrganizationUpdates)
    {
        const firstName = String(payload.first_name || '').trim();
        const lastName = String(payload.last_name || '').trim();
        const email = String(payload.email || '').trim();
        const updatePayload = {
            first_name: firstName,
            last_name: lastName,
            email,
            ...(payload.temporary_password ? { temporary_password: payload.temporary_password } : {}),
        };
        const { data } = await apiClient.put<UserListItem>(
            API_ENDPOINTS.USER_DETAIL(id),
            updatePayload
        );
        return data;
    }

    if (Array.isArray(payload.organization_memberships) && payload.organization_memberships.length > 0)
    {
        const targetOrganizationIds = normalizeOrganizationIds(
            payload.organization_memberships.map((membership) => membership.organization_id)
        );
        if (targetOrganizationIds.length === 0)
        {
            throw new Error('At least one organization is required');
        }

        const firstName = String(payload.first_name || '').trim();
        const lastName = String(payload.last_name || '').trim();
        const email = String(payload.email || '').trim();
        const updatePayload = {
            first_name: firstName,
            last_name: lastName,
            email,
            organization_memberships: payload.organization_memberships,
            ...(payload.temporary_password ? { temporary_password: payload.temporary_password } : {}),
        };
        const { data } = await apiClient.put<UserListItem>(
            API_ENDPOINTS.USER_DETAIL(id),
            updatePayload,
            buildOrganizationScopedConfig(targetOrganizationIds[0])
        );
        return data;
    }

    const currentOrganizationIds = normalizeOrganizationIds([
        payload.organization_id,
        ...(payload.current_organization_ids || []),
        ...(payload.organizations || []).map((membership) => membership.organization_id),
    ]);
    const targetOrganizationIds = payload.organization_ids
        ? normalizeOrganizationIds(payload.organization_ids)
        : currentOrganizationIds;

    if (targetOrganizationIds.length === 0)
    {
        throw new Error('At least one organization is required');
    }

    const role = payload.is_admin ? 'admin' : 'user';
    const firstName = String(payload.first_name || '').trim();
    const lastName = String(payload.last_name || '').trim();
    const email = String(payload.email || '').trim();

    const membershipPayload: MembershipPayload = {
        email,
        full_name: `${firstName} ${lastName}`.trim() || undefined,
        team_name: '',
        role,
    };

    const organizationIdsToAdd = targetOrganizationIds.filter((orgId) => !currentOrganizationIds.includes(orgId));
    const organizationIdsToRemove = currentOrganizationIds.filter((orgId) => !targetOrganizationIds.includes(orgId));

    if (organizationIdsToAdd.length > 0)
    {
        await addUserMembership(organizationIdsToAdd, membershipPayload);
    }

    let updatedUser: UserListItem | null = null;
    for (const orgId of targetOrganizationIds)
    {
        const updatePayload = {
            first_name: firstName,
            last_name: lastName,
            email,
            role,
            organization_id: orgId,
            ...(payload.temporary_password ? { temporary_password: payload.temporary_password } : {}),
        };
        const { data } = await apiClient.put<UserListItem>(
            API_ENDPOINTS.USER_DETAIL(id),
            updatePayload,
            buildOrganizationScopedConfig(orgId)
        );
        updatedUser = data;
    }

    for (const orgId of organizationIdsToRemove)
    {
        await removeUserMembership(orgId, id);
    }

    if (!updatedUser)
    {
        throw new Error('Failed to update user');
    }

    return updatedUser;
}

export async function deleteUser(id: number | string)
{
    await apiClient.delete(API_ENDPOINTS.USER_DETAIL(id));
}

export async function previewSelfDeletion(): Promise<SelfDeletionPreview>
{
    const { data } = await apiClient.get<SelfDeletionPreview>(`${API_ENDPOINTS.USERS}/me/deletion-preview`);
    return data;
}

export async function deleteCurrentUser(): Promise<void>
{
    await apiClient.delete(`${API_ENDPOINTS.USERS}/me`);
}

export interface CreateUserRequest
{
    first_name: string;
    last_name: string;
    email: string;
    temporary_password: string;
    is_admin: boolean;
    organization_name?: string;
    organization_names?: string[];
    organization_id?: number;
    organization_ids?: number[];
    organization_memberships?: OrganizationMembershipRequest[];
}

export async function createUser(payload: CreateUserRequest)
{
    const organizationIds = await resolveOrganizationIds(payload);
    if (organizationIds.length === 0)
    {
        throw new Error('Organization context not found for creating a user');
    }

    const requestBody = buildMembershipPayload(payload);
    const { data } = await addUserMembership(organizationIds, requestBody);
    return data as UserListItem;
}

export async function bulkDeleteUsers(ids: Array<number | string>)
{
    const { data } = await apiClient.post<{ deleted: number }>(
        API_ENDPOINTS.USERS_BULK_DELETE,
        { ids }
    );

    return data;
}

export async function importUsersCsv(csvContent: string)
{
    const { data } = await apiClient.post(
        API_ENDPOINTS.USERS_IMPORT_CSV,
        { content: csvContent }
    );

    return data as { created: number; skipped: number; errors: string[] };
}
